import axios from "axios";
import { getElevationToken, clearElevation } from "./elevation";

const axiosClient = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/admin`,
  headers: {
    "Content-Type": "application/json",
  },
});

axiosClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axiosClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.response &&
      error.response.data &&
      error.response.data.status_code === 403 &&
      error.response.data.message === "Invalid token"
    ) {
      localStorage.clear();

    }
    return Promise.reject(error);
  }
);

const axiosAppClient = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/api`,
  headers: {
    "Content-Type": "application/json",
  },
});

axiosAppClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axiosAppClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.response &&
      error.response.data &&
      error.response.data.status_code === 403 &&
      error.response.data.message === "Invalid token"
    ) {
      localStorage.clear();

    }
    return Promise.reject(error);
  }
);
// ACS "Get reminded to renew your insurance" submissions from the ANSA App
// 60-day early-renewal block (ACS Dashboard BRD 3 July 2026).
const getEarlyRenewalReminders = (filters = {}) => {
  return axiosClient
    .post("/early-renewal-reminders/list", filters)
    .then((response) => {
      return (response.data && response.data.data) || response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const postLogin = (data) => {
  return axiosClient
    .post("/login", data)
    .then((response) => {
      if (response) {
        const { token, permissions,id } = response.data.data;
        localStorage.setItem("id", id);
        localStorage.setItem("token", token);
        localStorage.setItem("permissions", JSON.stringify(permissions));
        return response.data;
      }
      throw new Error("Token not found in response");
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        switch (err.response.status) {
          case 422:
            message = err.response.data.message || "Validation error";
            break;
          default:
            message = err.response.data.message || "An error occurred";
            break;
        }
      }
      throw new Error(message);
    });
};

const forgotPassword = (data) => {
  return axiosClient
    .post("/forgot-password", data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const resetPassword = (data) => {
  return axiosClient
    .post("/reset-password", data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const getProfile = () => {
  return axiosClient
    .post("/profile?lowercase=true")
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const updateProfile = (data) => {
  return axiosClient
    .post("/update-profile", data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const changePassword = (data) => {
  return axiosClient
    .post("/change-password", data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const getDashboardRegisteredUsers = (data) => {
  return axiosClient
    .post("/dashboard/get-total-registered-users", data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const getDashboardLoggedInUsers = (data) => {
  return axiosClient
    .post("/dashboard/get-total-logged-in-users", data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const getDashboardIncompleteProfileUsers = () => {
  return axiosClient
    .post("/dashboard/get-incomplete-profile-users", {})
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const getDashboardLiveLoggedInUsers = () => {
  return axiosClient
    .post("/dashboard/get-live-logged-in-users", {})
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const getDashboardTotalOrders = (data) => {
  return axiosClient
    .post("/dashboard/get-total-orders", data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};
const getDashboardTotalPolicies = (data) => {
  return axiosClient
    .post("/dashboard/get-total-policies", data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

// Password-gated dashboard amounts (ACS Dashboard BRD 3 July 2026): the two
// amount endpoints require the short-lived elevation token minted by
// /admin/verify-password, sent as X-Elevation-Token next to the Bearer token.
const withElevationHeader = () => {
  const elevationToken = getElevationToken();
  return elevationToken
    ? { headers: { "X-Elevation-Token": elevationToken } }
    : {};
};

const handleGatedError = (err) => {
  let message = "An error occurred";
  if (err.response) {
    message = err.response.data.message || "An error occurred";
    // Backend says the elevation is missing/expired — drop the stale token so
    // the dashboard re-masks and asks for the password again.
    if (message === "Password verification required") {
      clearElevation();
    }
  }
  throw new Error(message);
};

const verifyAdminPassword = (data) => {
  return axiosClient
    .post("/verify-password", data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const getDashboardTotalSales = (data) => {
  return axiosClient
    .post("/dashboard/get-total-sales", data, withElevationHeader())
    .then((response) => {
      return response.data;
    })
    .catch(handleGatedError);
}

const getDashboardTotalByPaymentMethod = (data) => {
  return axiosClient
    .post("/dashboard/get-total-by-payment-method", data, withElevationHeader())
    .then((response) => {
      return response.data;
    })
    .catch(handleGatedError);
}

const getDashboardTransactionsByInsurerProduct = (data) => {
  return axiosClient
    .post("/dashboard/get-transactions-by-insurer-product", data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
}

const getDashboardPolicyOutcomes = (data) => {
  return axiosClient
    .post("/dashboard/get-policy-outcomes", data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
}

const getDashboardTransactionVolume = (data) => {
  return axiosClient
    .post("/dashboard/get-transaction-volume", data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
}

const getDashboardTransactionAmounts = (data) => {
  return axiosClient
    .post("/dashboard/get-transaction-amounts", data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
}

const getDashboardPaymentMethodOutcomes = (data) => {
  return axiosClient
    .post("/dashboard/get-payment-method-outcomes", data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
}


const getDashboardQuotationFunnel = (data) => {
  return axiosClient
    .post("/dashboard/get-quotation-funnel", data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
}

const getAdmins = (filters = {}) => {
  return axiosClient
    .post("/list-admin", filters)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const getAdmin = (filters = {}) => {
  return axiosClient
    .post("/view-admin", filters)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const createAdmin = (data) => {
  return axiosClient
    .post("/create-admin", data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};
const editAdmin = (data) => {
  return axiosClient
    .post("/update-admin", data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const deleteAdmin = (payload) => {
  return axiosClient
    .post(`/delete-admin`, payload)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};
const getUsers = (filters = {}) => {
  return axiosClient
    .post("/user/list", filters)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const getUser = (filters = {}) => {
  return axiosClient
    .post("/user/details", filters)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const editUser = (data) => {
  return axiosClient
    .post("/user/update", data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const getRoleNames = () => {
  return axiosClient
    .post("/roles/names")
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const getRolePermissions = () => {
  return axiosClient
    .post("/roles/permissions")
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};


const getRolesList = (filters = {}) => {
  return axiosClient
    .post("/roles/list", filters)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const getRole = (data) => {
  return axiosClient
    .post("/roles/details", data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const storeRole = (data) => {
  return axiosClient
    .post("/roles/store", data)
    .then((response) => {
      return response.data;
    }
    )
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const updateRole = (data) => {
  return axiosClient
    .post("/roles/update", data)
    .then((response) => {
      return response.data;
    }
    )
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const deleteRole = (data) => {
  return axiosClient
    .post("/roles/delete", data)
    .then((response) => {
      return response.data;
    }
    )
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const getAuditLogs = (filters = {}) => {
  return axiosClient
    .post("/audit-log/list", filters)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const getOrders = (filters = {}) => {
  return axiosClient
    .post("/order/list", filters)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const getOrder = (data) => {
  return axiosClient
    .post("/order/details", data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const updateOrder = (data) => {
  return axiosClient
    .post("/order/update", data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const createOrder = (data) => {
  return axiosClient
    .post("/order/store", data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const uploadReceipts = (formData) => {
  return axiosClient
    .post("/order/upload-receipts", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        switch (err.response.status) {
          case 422:
            message = err.response.data.message || "Validation error";
            break;
          default:
            message = err.response.data.message || "An error occurred";
            break;
        }
      }
      throw new Error(message);
    });
};

const downloadReceipt = (data) => {
  return axiosAppClient
    .post("/order/download-receipt", data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
    });
};

const setPayableOrder = (data) => {
  return axiosClient
    .post("/order/set-total-payable", data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
    });
};

const getNotifications = (filters = {}) => {
  return axiosClient
    .post("/notification/list", filters)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const getProducts = (filters = {}) => {
  return axiosClient
    .post("/product/list", filters)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const getSubscriptions = (filters = {}) => {
  return axiosClient
    .post("/subscriptions/list", filters)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const createShorts = (formData) => {
  return axiosClient
    .post("/short/store", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const getShorts = (filters = {}) => {
  return axiosClient
    .post("/short/list", filters)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const updateShortStatus = (data = {}) => {
  return axiosClient
    .post("/short/change-status", data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const editShort = (formData) => {
  return axiosClient
    .post("/short/update", formData, {
      headers: {
        "Content-Type": "multipart/form-data"
      },
    })
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const viewShort = (data) => {
  return axiosClient
    .post("/short/details", data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const deleteShort = (data) => {
  return axiosClient
    .post("/short/delete", data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const getInsurancePlans = (filters = {}) => {
  return axiosClient
    .post("/insurance-plan/list", filters)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const getPolicies = (filters = {}) => {
  return axiosClient
    .post("/policies/list", filters)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const exportPolicies = (payload = {}) => {
  return axiosClient
    .post("/policies/export", payload, { responseType: "blob" })
    .then((response) => response.data);
};

const getPolicy = (data) => {
  return axiosClient
    .post("/policies/details", data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const createPolicy = (data) => {
  return axiosClient
    .post("/policies/store", data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const updatePolicy = (formData) => {
  return axiosClient
    .post("/policies/update", formData)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const uploadPolicyFiles = (formData) => {
  return axiosClient
    .post("/policies/upload-files", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const createBanner = (formData) => {
  return axiosClient
    .post("/carousel-banners", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const getBanners = (filters = {}) => {
  return axiosClient
    .get("/carousel-banners", { params: filters })
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const editBanner = (formData, id) => {
  return axiosClient
    .put(`/carousel-banners/${id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const deleteBanner = (id) => {
  return axiosClient
    .delete(`/carousel-banners/${id}`)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};
const viewBanner = (id) => {
  return axiosClient
    .get(`/carousel-banners/${id}`)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const uploadForeignQuote = (formData) => {
  return axiosClient
    .post("/foreign-worker-insurance/upload-quotation", formData)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        switch (err.response.status) {
          case 422:
            message = err.response.data.message || "Validation error";
            break;
          default:
            message = err.response.data.message || "An error occurred";
            break;
        }
      }
      throw new Error(message);
    });
};

const uploadPropertyQuote = (formData) => {
  return axiosClient
    .post("/property-insurance/upload-quotation", formData)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        switch (err.response.status) {
          case 422:
            message = err.response.data.message || "Validation error";
            break;
          default:
            message = err.response.data.message || "An error occurred";
            break;
        }
      }
      throw new Error(message);
    });
};

const uploadGroupQuote = (formData) => {
  return axiosClient
    .post("/group-insurance/upload-quotation", formData)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        switch (err.response.status) {
          case 422:
            message = err.response.data.message || "Validation error";
            break;
          default:
            message = err.response.data.message || "An error occurred";
            break;
        }
      }
      throw new Error(message);
    });
};

const uploadOffshoreQuote = (formData) => {
  return axiosClient
    .post("/offshore-insurance/upload-quotation", formData)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        switch (err.response.status) {
          case 422:
            message = err.response.data.message || "Validation error";
            break;
          default:
            message = err.response.data.message || "An error occurred";
            break;
        }
      }
      throw new Error(message);
    });
};

const uploadTermLifeQuote = (formData) => {
  return axiosClient
    .post("/term-life-insurance/upload-quotation", formData)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        switch (err.response.status) {
          case 422:
            message = err.response.data.message || "Validation error";
            break;
          default:
            message = err.response.data.message || "An error occurred";
            break;
        }
      }
      throw new Error(message);
    });
};

const  uploadForeignFile = (formData) => {
  return axiosClient
    .post("/foreign-worker-insurance/upload-file", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        switch (err.response.status) {
          case 422:
            message = err.response.data.message || "Validation error";
            break;
          default:
            message = err.response.data.message || "An error occurred";
            break;
        }
      }
      throw new Error(message);
    });
}

const uploadPropertyFile = (formData) => {
  return axiosClient
    .post("/property-insurance/upload-file", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        switch (err.response.status) {
          case 422:
            message = err.response.data.message || "Validation error";
            break;
          default:
            message = err.response.data.message || "An error occurred";
            break;
        }
      }
      throw new Error(message);
    });
};

const uploadGroupFile = (formData) => {
  return axiosClient
    .post("/group-insurance/upload-file", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        switch (err.response.status) {
          case 422:
            message = err.response.data.message || "Validation error";
            break;
          default:
            message = err.response.data.message || "An error occurred";
            break;
        }
      }
      throw new Error(message);
    });
};

const uploadOffshoreFile = (formData) => {
  return axiosClient
    .post("/offshore-insurance/upload-file", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        switch (err.response.status) {
          case 422:
            message = err.response.data.message || "Validation error";
            break;
          default:
            message = err.response.data.message || "An error occurred";
            break;
        }
      }
      throw new Error(message);
    });
};

const uploadTermLifeFile = (formData) => {
  return axiosClient
    .post("/term-life-insurance/upload-file", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        switch (err.response.status) {
          case 422:
            message = err.response.data.message || "Validation error";
            break;
          default:
            message = err.response.data.message || "An error occurred";
            break;
        }
      }
      throw new Error(message);
    });
};

const getQuotes = (filters = {}) => {
  return axiosClient
    .post("/quote/list", filters)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      throw err; // Re-throw the error so it can be handled by the caller
    });
}
const uploadQuoteQuotation = (formData) => {
  return axiosClient
    .post("/quote/upload-quotation", formData, {

    })
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      console.error("Error uploading quotation:", err);
      throw err; // Re-throw the error so it can be handled by the caller
    });
};
const deleteQuoteQuotation = (formData) => {
  return axiosClient
    .post("/quote/delete-quotation", formData, {

    })
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      console.error("Error uploading quotation:", err);
      throw err; // Re-throw the error so it can be handled by the caller
    });
};
const updateQuoteQuotation = (data) => {
  return axiosClient
    .post("/quote/update-quotation", data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
    });
}
const uploadQuoteFile = (formData) => {
  return axiosClient
    .post("/quote/upload-file", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      throw err; // Re-throw the error so it can be handled by the caller
    });
}

const getQuoteDetails = () => {
  return axiosClient
    .post("/quote/details")
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
    });
}

const exportQuoteRequests = (filters = {}) => {
  return axiosClient
    .post("/quote-request/export", filters, { responseType: "blob" })
    .then((response) => response.data);
};

const getQuoteRequests = (filters = {}) => {
  return axiosClient
    .post("/quote-request/list", filters)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
    });
}
const getQuoteRequest = (filters = {}) => {
  return axiosClient
    .post("/quote-request/details", filters)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
    });
}

const updateQuoteRequest = (data) => {
  return axiosClient
    .post("/quote-request/update", data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
    });
}

const saveDraft = (formData) => {
  return axiosClient
    .post("/quote/save", formData)
    .then((response) => response.data)
    .catch((err) => {
      console.error("Error saving draft:", err);
      throw err;
    });
};

 const revertStatus = (payload) => {
  return axiosClient
    .post("/quote/revert-status", payload)
    .then((response) => response.data)
    .catch((err) => {
      console.error("Error reverting status:", err);
      throw err;
    });
};

const getAppSettings = () => {
  return axiosClient
    .post("/settings/list")
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const updateAppSetting = (data) => {
  return axiosClient
    .post("/settings/update", data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) {
        message = err.response.data.message || "An error occurred";
      }
      throw new Error(message);
    });
};

const getMotorPredefined = (filters = {}) => {
  return axiosClient
    .post("/motor-predefined/list", filters)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
    });
}

const createMotorPredefined = (filters = {}) => {
  return axiosClient
    .post("/motor-predefined/create", filters)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
    });
}

const viewMotorPredefined = (filters = {}) => {
  return axiosClient
    .post("/motor-predefined/details", filters)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
    });
}

const updateMotorPredefined = (id, filters) => {
  return axiosClient
    .post(`/motor-predefined/update/${id}`, filters)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
    });
}

const findMotorPredefined = (filters = {}) => {
  return axiosClient
    .post("/motor-predefined/find", filters)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      let message = "An error occurred";
    });
}

const getPolicyReconciliation = (payload = {}) => {
  return axiosClient
    .post("/reports/policy-reconciliation", payload)
    .then((response) => response.data)
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) message = err.response.data.message || message;
      throw new Error(message);
    });
};

const getPolicyReconciliationSummary = (payload = {}) => {
  return axiosClient
    .post("/reports/policy-reconciliation/summary", payload)
    .then((response) => response.data)
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) message = err.response.data.message || message;
      throw new Error(message);
    });
};

const exportPolicyReconciliation = (payload = {}) => {
  return axiosClient
    .post("/reports/policy-reconciliation/export", payload, { responseType: "blob" })
    .then((response) => response.data);
};

const recordRefund = (payload = {}) => {
  return axiosClient
    .post("/order/refund", payload)
    .then((response) => response.data)
    .catch((err) => {
      let message = "An error occurred";
      if (err.response) message = err.response.data.message || message;
      throw new Error(message);
    });
};

// ---------------------------------------------------------------------------
// Paid but uninsured — Ansa-Support#49.
//
// Both of these reject on failure rather than resolving to an empty shape.
// That is the whole point of the screen: a panel that renders "nothing stuck"
// because the request failed is the exact defect #49 reports, where a monitor
// logged 0 stuck for nine days while a customer sat paid and uninsured.
//
// The message is derived from the status rather than relayed from the response
// body, so a 500 cannot put a backend string on a shared screen.
// ---------------------------------------------------------------------------
const postPaidUninsured = (endpoint, payload = {}) => {
  return axiosClient
    .post(endpoint, payload)
    .then((response) => response.data)
    .catch((err) => {
      const status = err.response && err.response.status;
      let message = "Could not reach the server";
      if (status === 401 || status === 403) message = "Not authorised to view this report";
      else if (status) message = `The server could not complete this check (HTTP ${status})`;
      throw new Error(message);
    });
};

const getPaidUninsured = (payload = {}) => postPaidUninsured("/reports/paid-uninsured", payload);
const getPaidUninsuredSummary = (payload = {}) =>
  postPaidUninsured("/reports/paid-uninsured/summary", payload);

export {
  axiosClient as adminAxios,
  getDashboardRegisteredUsers,
  getDashboardLoggedInUsers,
  getDashboardIncompleteProfileUsers,
  getDashboardLiveLoggedInUsers,
  getDashboardTotalOrders,
  getDashboardTotalPolicies,
  getDashboardTotalSales,
  getDashboardTotalByPaymentMethod,
  getDashboardTransactionsByInsurerProduct,
  getDashboardPolicyOutcomes,
  getDashboardTransactionVolume,
  getDashboardTransactionAmounts,
  getDashboardPaymentMethodOutcomes,
  getDashboardQuotationFunnel,
  postLogin,
  forgotPassword,
  resetPassword,
  getProfile,
  updateProfile,
  changePassword,
  verifyAdminPassword,
  getAdmins,
  getAdmin,
  editAdmin,
  createAdmin,
  deleteAdmin,
  getUsers,
  getUser,
  editUser,
  getRoleNames,
  getRolesList,
  getRole,
  storeRole,
  updateRole,
  deleteRole,
  getRolePermissions,
  getAuditLogs,
  getOrders,
  getOrder,
  updateOrder,
  createOrder,
  uploadReceipts,
  downloadReceipt,
  setPayableOrder,
  getNotifications,
  getProducts,
  getSubscriptions,
  createShorts,
  getShorts,
  updateShortStatus,
  editShort,
  viewShort,
  deleteShort,
  getInsurancePlans,
  getPolicies,
  exportPolicies,
  getPolicy,
  createPolicy,
  updatePolicy,
  uploadPolicyFiles,
  createBanner,
  getBanners,
  editBanner,
  deleteBanner,
  viewBanner,
  uploadForeignQuote,
  uploadPropertyQuote,
  uploadGroupQuote,
  uploadOffshoreQuote,
  uploadTermLifeQuote,
  uploadForeignFile,
  uploadPropertyFile,
  uploadGroupFile,
  uploadOffshoreFile,
  uploadTermLifeFile,
  getQuotes,
  uploadQuoteQuotation,
  deleteQuoteQuotation,
  updateQuoteQuotation,
  uploadQuoteFile,
  getQuoteDetails,
  getQuoteRequests,
  exportQuoteRequests,
  getQuoteRequest,
  updateQuoteRequest,
  saveDraft,
  revertStatus,
  getMotorPredefined,
  createMotorPredefined,
  viewMotorPredefined,
  updateMotorPredefined,
  findMotorPredefined,
  getAppSettings,
  getEarlyRenewalReminders,
  updateAppSetting,
  getPolicyReconciliation,
  getPolicyReconciliationSummary,
  exportPolicyReconciliation,
  recordRefund,
  getPaidUninsured,
  getPaidUninsuredSummary,
};
